import { useState, useEffect, lazy, Suspense } from "react";
import { db } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./RegisterStudent.css";

// Lazy load both navbars
const Navbar = lazy(() => import("./Navbar"));
const NavbarApp = lazy(() => import("./NavbarApp"));

const RegisterStudent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [companyName, setCompanyName] = useState("Loading...");
  const [companyCode, setCompanyCode] = useState("");
  const [studentsList, setStudentsList] = useState([]);
  const [student, setStudent] = useState({
    studentId: "",
    fullName: "",
    email: "",
    githubURL: "",
  });
  const [searchQuery, setSearchQuery] = useState("");

  // Conditionally load Navbar
  const NavbarComponent = location.pathname.includes("/app")
    ? NavbarApp
    : Navbar;

  const filteredStudents = studentsList.filter((s) =>
    (s.fullName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.studentId || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 16;

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstStudent, indexOfLastStudent);
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);


  const calculateNextId = (list) => {
    if (!list || list.length === 0) return "1";
    const numericIds = list
      .map((s) => parseInt(s.studentId, 10))
      .filter((val) => !isNaN(val));
    const highest = numericIds.length > 0 ? Math.max(...numericIds) : 0;
    return (highest + 1).toString();
  };

  // Prefill studentId if roster loads or changes and admin is not in edit mode
  useEffect(() => {
    if (!student.fullName && !student.email && !student.githubURL) {
      setStudent((prev) => ({
        ...prev,
        studentId: calculateNextId(studentsList),
      }));
    }
  }, [studentsList, student.fullName, student.email, student.githubURL]);


  // ******************
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const companyRef = doc(db, "CorporateClients", companyCode);
        const companySnap = await getDoc(companyRef);
        if (companySnap.exists()) {
          setCompanyName(companySnap.data().companyName || "Unknown");
        } else {
          setCompanyName("Unknown");
        }
      } catch (error) {
        console.error("Error fetching company name:", error);
        setCompanyName("Error");
      }
    };

    if (companyCode) {
      fetchCompanyName();
    }
  }, [companyCode]);

  //*******************


  useEffect(() => {
    const storedCompanyCode = localStorage.getItem("orgCode") || params.orgCode ;

    if (!storedCompanyCode) {
      alert("Company code missing. Please login again.");
      navigate("/");
    } else {
      setCompanyCode(storedCompanyCode);
      localStorage.setItem("companyCode", storedCompanyCode);
    }
  }, [params.orgCode, navigate]);

  const fetchStudentsList = async () => {
    if (!companyCode) return;
    try {
      const querySnapshot = await getDocs(
        collection(db, `CorporateClients/${companyCode}/studentInfo`)
      );
      const list = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      list.sort((a, b) => {
        const idA = parseInt(a.studentId, 10);
        const idB = parseInt(b.studentId, 10);
        return idB - idA; // descending
      });
      setStudentsList(list);
    } catch (error) {
      console.error("Error fetching students list:", error);
    }
  };

  useEffect(() => {
    fetchStudentsList();
  }, [companyCode]);

  const handleStudentSelect = async (studentId) => {
    if (!companyCode || !studentId) return;

    try {
      const docRef = doc(
        db,
        `CorporateClients/${companyCode}/studentInfo`,
        studentId
      );
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setStudent({
          studentId: data.studentId || "",
          fullName: data.fullName || "",
          email: data.email || "",
          githubURL: data.githubURL || "",
        });
      } else {
        alert("Student not found.");
      }
    } catch (error) {
      console.error("Error fetching student:", error);
    }
  };

  const handleChange = (e) => {
    setStudent({ ...student, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !student.studentId.trim() ||
      !student.fullName.trim()
    ) {
      alert("Please fill in all fields.");
      return;
    }

    try {
      const docRef = doc(
        db,
        `CorporateClients/${companyCode}/studentInfo`,
        student.studentId
      );

      await setDoc(
        docRef,
        {
          studentId: student.studentId,
          fullName: student.fullName,
          email: student.email,
          githubURL: student.githubURL,
          attendance: {},
          marks: {},
        },
        { merge: true }
      );

      alert("Student registered/updated successfully!");
      setStudent({ studentId: "", fullName: "", email: "", githubURL: "", });
      fetchStudentsList();
    } catch (error) {
      console.error("Error saving student:", error);
      alert("Failed to save student.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;

    try {
      await deleteDoc(
        doc(db, `CorporateClients/${companyCode}/studentInfo`, id)
      );
      alert("Student deleted successfully.");
      fetchStudentsList();
    } catch (error) {
      console.error("Error deleting student:", error);
      alert("Failed to delete student.");
    }
  };

  return (
    <>
      <Suspense fallback={<div>Loading Navbar...</div>}>
        <NavbarComponent companyName={companyName} />
      </Suspense>
      <div className="register-page">
        {/* Left Section */}
        <div className="left-section">
          <h3>
            <span>Registered Students</span>
            <span className="student-count">{studentsList.length}</span>
          </h3>

          <div className="search-bar-container">
            <input
              type="text"
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="student-search-input"
            />
          </div>

          <div className="students-list-container">
            {currentStudents.length > 0 ? (
              currentStudents.map((s) => (
                <div
                  key={s.studentId}
                  className="student-item"
                  onClick={() => handleStudentSelect(s.studentId)}
                >
                  <div className="student-info-meta">
                    <span className="name">{s.fullName}</span>
                    <span className="id">ID: {s.studentId}</span>
                  </div>
                  <button
                    className="delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(s.studentId);
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))
            ) : (
              <p className="no-students-msg">
                {searchQuery ? "No matching students found." : "No students registered."}
              </p>
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination-container">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                className="pagination-btn"
              >
                &larr; Prev
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                className="pagination-btn"
              >
                Next &rarr;
              </button>
            </div>
          )}
        </div>

        {/* Right Section */}
        <div className="right-section">
          <h2>Register or Update Student</h2>
          <form onSubmit={handleSubmit} className="register-form">
            <input
              type="text"
              name="studentId"
              placeholder="Student ID"
              value={student.studentId}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="fullName"
              placeholder="Full Name"
              value={student.fullName}
              onChange={handleChange}
              required
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={student.email}
              onChange={handleChange}

            />
            <input
              type="url"
              name="githubURL"
              placeholder="GitHub URL"
              value={student.githubURL}
              onChange={handleChange}
            />
            <button type="submit">Register / Update</button>
          </form>
        </div>
      </div>
    </>
  );
};

export default RegisterStudent;
